import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, ClipboardList, Users, Inbox, KeyRound, ArrowLeft, Sparkles, LogOut, ShoppingBag, FileText } from "lucide-react";
import { useAuth } from "../lib/auth";

const links = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true, testid: "admin-side-overview" },
  { to: "/admin/orders", label: "Orders queue", icon: ClipboardList, testid: "admin-side-orders" },
  { to: "/admin/customers", label: "Customers", icon: Users, testid: "admin-side-customers" },
  { to: "/admin/leads", label: "Leads", icon: Inbox, testid: "admin-side-leads" },
  { to: "/admin/blog", label: "Blog", icon: FileText, testid: "admin-side-blog" },
  { to: "/admin/access", label: "Access", icon: KeyRound, testid: "admin-side-access" },
];

export default function AdminLayout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="dashboard-theme min-h-screen bg-bg" data-testid="admin-layout">
      <div className="grid lg:grid-cols-[256px_1fr] min-h-screen">
        <aside className="hidden lg:flex flex-col border-r border-line bg-ink text-white">
          <Link to="/" className="flex items-center gap-2 px-6 h-16 border-b border-white/10">
            <div className="h-8 w-8 rounded-xl bg-terra text-white grid place-items-center"><Sparkles size={16} /></div>
            <div>
              <div className="font-display font-bold tracking-tight">Zenith Ranking</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/50">Admin console</div>
            </div>
          </Link>
          <div className="p-4 flex flex-col gap-1">
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${
                      isActive ? "bg-white text-ink font-semibold" : "text-white/70 hover:bg-white/10 hover:text-white"
                    }`
                  }
                  data-testid={l.testid}
                >
                  <Icon size={16} />
                  {l.label}
                </NavLink>
              );
            })}
          </div>
          <div className="mt-auto p-4 border-t border-white/10 space-y-3">
            <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/70 hover:text-white">
              <ShoppingBag size={14} /> My customer view
            </Link>
            <Link to="/" className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-white/70 hover:text-white">
              <ArrowLeft size={14} /> Back to site
            </Link>
            <div className="px-3 py-3 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-terra text-white grid place-items-center text-sm font-semibold">
                  {(user?.name || "A").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{user?.name}</div>
                  <div className="text-xs text-white/60 truncate">{user?.email}</div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs text-white/70 hover:text-white py-2 rounded-lg border border-white/10"
                data-testid="admin-logout"
              >
                <LogOut size={12} /> Log out
              </button>
            </div>
          </div>
        </aside>

        <div className="flex flex-col">
          <header className="lg:hidden h-14 px-4 border-b border-line flex items-center justify-between bg-ink text-white">
            <Link to="/admin" className="flex items-center gap-2 font-display font-bold">
              <Sparkles size={16} /> Zenith Ranking admin
            </Link>
            <button onClick={handleLogout} className="text-xs text-white/70" data-testid="admin-mobile-logout">Log out</button>
          </header>
          <div className="p-6 lg:p-10 max-w-7xl w-full">{children}</div>
        </div>
      </div>
    </div>
  );
}
